import React, { useState, useEffect } from 'react';
import { 
  getNotices, 
  createNotice, 
  updateNotice, 
  deleteNotice, 
  Notice 
} from '../api/admin';
import client from '../api/client';
import styles from './CommonPage.module.css'; // Reuse common styles
import { Plus, Edit, Trash2, Eye, EyeOff, ChevronLeft, ChevronRight } from 'lucide-react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
// @ts-ignore
import ImageResize from 'quill-image-resize-module-react';

Quill.register('modules/imageResize', ImageResize);

export default function NoticePage() {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    isVisible: true
  });

  useEffect(() => {
    fetchNotices();
  }, []);

  const fetchNotices = async (pageIdx: number = 0) => {
    try {
      const data = await getNotices(pageIdx, 10);
      setNotices(data.content);
      setTotalPages(data.totalPages);
      setPage(data.number);
    } catch (error) {
      console.error('Failed to fetch notices', error);
      alert('공지사항 목록을 불러오지 못했습니다.');
    }
  };

  const handleCreate = () => {
    setEditingId(null);
    setFormData({ title: '', content: '', isVisible: true });
    setIsModalOpen(true);
  };

  const handleEdit = (notice: Notice) => {
    setEditingId(notice.id);
    setFormData({ 
      title: notice.title, 
      content: notice.content, 
      isVisible: notice.isVisible 
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    try {
      await deleteNotice(id);
      fetchNotices();
    } catch (error) {
      console.error('Failed to delete', error);
      alert('삭제에 실패했습니다.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.content) {
      alert('제목과 내용을 입력해주세요.');
      return;
    }

    try {
      if (editingId) {
        await updateNotice(editingId, formData);
      } else {
        await createNotice(formData);
      }
      setIsModalOpen(false);
      fetchNotices();
    } catch (error) {
      console.error('Failed to save', error);
      alert('저장 실패했습니다.');
    }
  };



  // Wait, I need access to Quill instance to insertEmbed.
  // So I'll need `const quillRef = React.useRef<ReactQuill>(null);`
  // And `const range = quillRef.current.getEditor().getSelection();`
  // `quillRef.current.getEditor().insertEmbed(range.index, 'image', url);`
  
  // Let's redefine implementation plan for this File.
  // 1. Add quillRef.
  // 2. Define imageHandler properly.
  // 3. Memoize modules.
  
// ...
  const quillRef = React.useRef<ReactQuill>(null);

  const imageHandler = React.useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      if (input.files && input.files[0]) {
        const file = input.files[0];
        const formData = new FormData();
        formData.append('image', file);

        try {
            const response = await client.post('/v1/notices/upload-image', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            const url = response.data;
            
            const quill = quillRef.current?.getEditor();
            if (quill) {
                const range = quill.getSelection(true);
                quill.insertEmbed(range.index, 'image', url);
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            alert('이미지 업로드 중 오류가 발생했습니다.');
        }
      }
    };
  }, []);

  const modules = React.useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    },
    imageResize: {
      modules: ['Resize', 'DisplaySize'] // Resize handles and display size overlay
    }
  }), [imageHandler]);

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image'
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>공지사항 관리</h1>
        <button className={styles.primaryBtn} onClick={handleCreate}>
          <Plus size={20} />
          공지 등록
        </button>
      </div>

      <div className={styles.tableContainer}>
        <div style={{ overflowX: 'auto' }}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>ID</th>
                <th>제목</th>
                <th style={{ textAlign: 'center' }}>공개</th>
                <th>작성일</th>
                <th style={{ textAlign: 'center' }}>관리</th>
              </tr>
            </thead>
            <tbody>
              {notices.map((notice) => (
                <tr key={notice.id}>
                  <td>{notice.id}</td>
                  <td style={{ textAlign: 'left', fontWeight: 'bold' }}>{notice.title}</td>
                  <td style={{ textAlign: 'center' }}>
                    {notice.isVisible ? (
                      <span style={{ color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                        <Eye size={16} /> 
                        공개
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                        <EyeOff size={16} /> 
                        비공개
                      </span>
                    )}
                  </td>
                  <td>{new Date(notice.createdAt).toLocaleDateString()}</td>
                  <td style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <button 
                        onClick={() => handleEdit(notice)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6' }}
                      >
                        <Edit size={18} />
                      </button>
                      <button 
                        onClick={() => handleDelete(notice.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {notices.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                    등록된 공지사항이 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className={styles.pagination}>
        <button disabled={page === 0} onClick={() => fetchNotices(page - 1)}>
          <ChevronLeft size={16} /> 이전
        </button>
        <span>{page + 1} / {totalPages > 0 ? totalPages : 1}</span>
        <button disabled={page >= totalPages - 1} onClick={() => fetchNotices(page + 1)}>
          다음 <ChevronRight size={16} />
        </button>
      </div>

      {isModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal} style={{ maxWidth: '800px' }}>
            <h3>{editingId ? '공지 수정' : '공지 등록'}</h3>
            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label>제목</label>
                <input 
                  type="text" 
                  
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="제목을 입력하세요"
                />
              </div>
              


              <div className={styles.formGroup}>
                <label>내용</label>
                <div style={{ height: '400px', marginBottom: '50px' }}>
                  <ReactQuill 
                    ref={quillRef}
                    theme="snow"
                    value={formData.content}
                    onChange={(content) => setFormData({...formData, content})}
                    modules={modules}
                    formats={formats}
                    style={{ height: '350px' }}
                  />
                </div>
              </div>

              <div className={styles.formGroup} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                <input 
                  type="checkbox" 
                  id="isVisible"
                  checked={formData.isVisible}
                  onChange={(e) => setFormData({...formData, isVisible: e.target.checked})}
                  style={{ width: '20px', height: '20px' }}
                />
                <label htmlFor="isVisible" style={{ marginBottom: 0, cursor: 'pointer' }}>공개 설정</label>
              </div>

              <div className={styles.formActions}>
                <button type="button" className={styles.secondaryBtn} onClick={() => setIsModalOpen(false)}>
                  취소
                </button>
                <button type="submit" className={styles.primaryBtn}>
                  저장
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
